import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from '../../utils/axios';
import {
  MP_LABEL,
  asList,
  forcedItems,
  parseForcedValue,
} from './msAssortmentApi.mjs';
import {
  MODE_SOURCE_LABEL,
  STOCK_MODE_OPTIONS,
  effectiveMode,
  ruPlural,
  stockModeChipProps,
  stockModeLabel,
} from './msProductsModel';

const MISSING_GROUP = 'missing';

const ORPHAN_HINT = 'товар не привязан к этому кабинету — настройка никуда не уходит';

/** Черновик строки диалога по элементу с сервера: mode 'default'|'live'|'forced'|'none' + число. */
function draftFromItem(item) {
  const mode = item.mode || 'default';
  return { mode, value: mode === 'forced' && item.forced_stock != null ? String(item.forced_stock) : '' };
}

function draftsEqual(a, b) {
  if (!a || !b) return a === b;
  if (a.mode !== b.mode) return false;
  if (a.mode !== 'forced') return true;
  return parseForcedValue(a.value) === parseForcedValue(b.value);
}

/** Что уйдёт на МП при таком черновике (подсказка под строкой). */
function previewLabel(draft, item) {
  if (draft.mode === 'forced') {
    const n = parseForcedValue(draft.value);
    return n == null ? 'введите число' : `на МП уйдёт ${n}`;
  }
  if (draft.mode === 'live') return 'живой остаток МС + добавка по техкартам';
  if (draft.mode === 'none') return 'на МП уйдёт 0';
  const def = item.binding_default_mode === 'none' ? 'не транслировать (0)' : 'живой остаток';
  return `режим связки: ${def}`;
}

function fieldHelper(draft, item) {
  const state = previewLabel(draft, item);
  if (!item.orphan) return state;
  const hint = item.binding_missing ? 'складская связка удалена — настройка никуда не уходит' : ORPHAN_HINT;
  return `${state}; ${hint}`;
}

/** Группы по кабинету: связки кабинетов со связями + сиротские записи. */
export function buildForcedGroups(items, links) {
  const groups = new Map();
  const ensure = (key, base) => {
    if (!groups.has(key)) groups.set(key, { key, ...base, items: [] });
    return groups.get(key);
  };
  for (const link of links || []) {
    ensure(`acc:${link.account_id}`, {
      marketplace: link.marketplace,
      account_id: link.account_id,
      account_name: link.account_name || null,
      linked: true,
    });
  }
  for (const item of items || []) {
    if (item?.binding_id == null) continue;
    const group = item.binding_missing || item.account_id == null
      ? ensure(MISSING_GROUP, { marketplace: null, account_id: null, account_name: null, linked: false })
      : ensure(`acc:${item.account_id}`, {
        marketplace: item.marketplace,
        account_id: item.account_id,
        account_name: item.account_name || null,
        linked: !item.orphan,
      });
    if (!group.account_name && item.account_name) group.account_name = item.account_name;
    group.items.push(item);
  }
  return [...groups.values()].sort((a, b) => Number(!a.linked) - Number(!b.linked));
}

function groupTitle(group) {
  if (group.key === MISSING_GROUP) return 'Удалённые складские связки';
  const mp = MP_LABEL[group.marketplace] || group.marketplace || '';
  return `${group.account_name || group.account_id}${mp ? ` · ${mp}` : ''}`;
}

export function MsProductForcedDialog({ open, row, onClose, onSaved, notify }) {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!row?.ms_id) {
      setItems([]);
      setDrafts({});
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios
        .get(`/ms-assortment/products/${encodeURIComponent(row.ms_id)}/forced`, { silent: true })
        .catch(() => ({ data: { items: forcedItems(row) } }));
      const list = asList(data).filter((item) => item?.binding_id != null);
      const next = {};
      for (const item of list) next[String(item.binding_id)] = draftFromItem(item);
      setItems(list);
      setDrafts(next);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось загрузить режимы остатка');
    } finally {
      setLoading(false);
    }
  }, [row, notify]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const groups = buildForcedGroups(items, row?.links);
  const orphans = items.filter((item) => item.orphan && item.mode != null);
  const draftFor = (item) => drafts[String(item.binding_id)] || draftFromItem(item);
  const setDraft = (item, patch) => setDrafts((prev) => ({
    ...prev,
    [String(item.binding_id)]: { ...draftFor(item), ...patch },
  }));

  const changedItems = () => items
    .filter((item) => !draftsEqual(draftFor(item), draftFromItem(item)))
    .map((item) => {
      const d = draftFor(item);
      return {
        binding_id: Number(item.binding_id),
        mode: d.mode,
        forced_stock: d.mode === 'forced' ? parseForcedValue(d.value) : null,
      };
    });
  const invalid = items.some((item) => {
    const d = draftFor(item);
    return d.mode === 'forced' && parseForcedValue(d.value) == null;
  });

  const put = async (payload, successText) => {
    setSaving(true);
    try {
      await axios.put(`/ms-assortment/products/${encodeURIComponent(row.ms_id)}/forced`, { items: payload });
      notify.success(successText);
      onSaved?.();
      return true;
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось сохранить режимы остатка');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const payload = changedItems();
    if (!payload.length) {
      onClose();
      return;
    }
    if (invalid) {
      notify.warning('Для режима «Принуд» укажите число');
      return;
    }
    if (await put(payload, 'Режимы остатка сохранены')) onClose();
  };

  const clearOrphans = async () => {
    const payload = orphans.map((item) => ({ binding_id: Number(item.binding_id), mode: 'default', forced_stock: null }));
    if (await put(payload, `Сиротские настройки сняты: ${payload.length}`)) load();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Остаток по складам · {row?.name || row?.ms_id}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Режим задаётся на товар МС и складскую связку кабинета, не на карточку МП.
            «Как у связки» — режим по умолчанию связки (настраивается в «Склады» → «Связь с МП»);
            «Живой остаток» — остаток МС плюс добавка по техкартам; «Принуд N» — всегда N (0 — честный ноль);
            «Не транслировать» — на склады связки уходит 0 (например, МГТ-товар на КГТ-складе).
          </Typography>
          {loading && <Typography variant="body2">Загружаем связки…</Typography>}
          {!loading && !groups.length && (
            <Alert severity="info">Нет связей с кабинетами МП — сначала привяжите карточку.</Alert>
          )}
          {!loading && orphans.length > 0 && (
            <Alert
              severity="warning"
              action={(
                <Button color="inherit" size="small" disabled={saving} onClick={clearOrphans}>
                  Снять все сиротские
                </Button>
              )}
            >
              Настроек без связи: {orphans.length}. Товар не привязан к этим кабинетам — значения никуда не уходят.
            </Alert>
          )}
          {!loading && groups.map((group) => (
            <Stack key={group.key} spacing={1.25}>
              <Typography variant="subtitle2">{groupTitle(group)}</Typography>
              {group.items.length === 0 && (
                <Typography variant="body2" color="text.secondary">У кабинета нет складских связок</Typography>
              )}
              {group.items.map((item) => {
                const id = String(item.binding_id);
                const d = draftFor(item);
                const eff = effectiveMode(item);
                const label = item.binding_missing ? `Связка ${id} (удалена)` : (item.binding_name || `Связка ${id}`);
                return (
                  <Stack key={id} spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <Typography variant="body2" fontWeight={500} sx={{ minWidth: 140 }} color={item.orphan ? 'warning.dark' : undefined}>
                        {label}
                      </Typography>
                      <Chip size="small" {...stockModeChipProps(eff)} label={`сейчас: ${stockModeLabel(eff)}`} sx={{ height: 20, fontSize: 11 }} />
                      <Typography variant="caption" color="text.secondary">{MODE_SOURCE_LABEL[eff.source]}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                        <InputLabel id={`mode-${id}`}>Режим</InputLabel>
                        <Select
                          labelId={`mode-${id}`}
                          label="Режим"
                          value={d.mode}
                          onChange={(e) => setDraft(item, { mode: e.target.value })}
                          disabled={saving}
                        >
                          {STOCK_MODE_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.value === 'default' && item.binding_default_mode
                                ? `Как у связки (${item.binding_default_mode === 'none' ? 'не транслировать' : 'живой'})`
                                : o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        type="number"
                        label="Принуд"
                        value={d.value}
                        onChange={(e) => setDraft(item, { value: e.target.value, mode: 'forced' })}
                        disabled={saving || d.mode !== 'forced'}
                        error={d.mode === 'forced' && parseForcedValue(d.value) == null}
                        inputProps={{ min: 0 }}
                        sx={{ width: 120 }}
                      />
                    </Stack>
                    <Typography variant="caption" color={item.orphan ? 'warning.dark' : 'text.secondary'}>
                      {fieldHelper(d, item)}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={saving || loading || !items.length}>
          {saving ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Группировка всех связок по кабинету для выбора области массовой операции. */
function groupBindings(bindings) {
  const byAcc = new Map();
  for (const b of bindings || []) {
    const key = `${b.marketplace}:${b.account_id}`;
    if (!byAcc.has(key)) {
      byAcc.set(key, {
        key,
        marketplace: b.marketplace,
        account_id: b.account_id,
        account_name: b.account_name || `Кабинет ${b.account_id}`,
        account_active: b.account_active !== false,
        bindings: [],
      });
    }
    byAcc.get(key).bindings.push(b);
  }
  return [...byAcc.values()];
}

export function MsBulkForcedDialog({ open, msIds, onClose, onSaved, notify }) {
  const [mode, setMode] = useState('forced');
  const [value, setValue] = useState('');
  const [scope, setScope] = useState('all');
  const [bindingIds, setBindingIds] = useState([]);
  const [includeVariants, setIncludeVariants] = useState(true);
  const [bindings, setBindings] = useState([]);
  const [loadingBindings, setLoadingBindings] = useState(false);
  const [saving, setSaving] = useState(false);
  const count = Array.isArray(msIds) ? msIds.length : 0;

  useEffect(() => {
    if (!open) return;
    setMode('forced');
    setValue('');
    setScope('all');
    setBindingIds([]);
    setIncludeVariants(true);
    let cancelled = false;
    setLoadingBindings(true);
    axios.get('/ms-assortment/bindings', { silent: true })
      .then(({ data }) => { if (!cancelled) setBindings(asList(data)); })
      .catch(() => { if (!cancelled) setBindings([]); })
      .finally(() => { if (!cancelled) setLoadingBindings(false); });
    return () => { cancelled = true; };
  }, [open]);

  const groups = useMemo(() => groupBindings(bindings), [bindings]);
  const bindingById = useMemo(() => new Map(bindings.map((b) => [Number(b.id), b])), [bindings]);
  const forcedInvalid = mode === 'forced' && parseForcedValue(value) == null;
  const scopeInvalid = scope === 'selected' && !bindingIds.length;

  const apply = async () => {
    if (!count) {
      notify.warning('Выделите товары');
      return;
    }
    if (forcedInvalid) {
      notify.warning('Укажите число принуда');
      return;
    }
    if (scopeInvalid) {
      notify.warning('Выберите хотя бы одну складскую связку');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ms_ids: msIds,
        mode,
        forced_stock: mode === 'forced' ? parseForcedValue(value) : null,
        include_variants: includeVariants,
      };
      if (scope === 'selected') payload.binding_ids = bindingIds.map(Number);
      const { data } = await axios.put('/ms-assortment/products/forced', payload);
      const applied = Number(data?.applied) || 0;
      const skipped = Array.isArray(data?.skipped) ? data.skipped.length : 0;
      const rows = Number(data?.rows) || 0;
      const variants = Number(data?.variants_added) || 0;
      const what = STOCK_MODE_OPTIONS.find((o) => o.value === mode)?.label || mode;
      const tail = `${variants ? `, модификаций добавлено ${variants}` : ''}. Строк: ${rows}`;
      if (skipped && applied) {
        notify.warning(`«${what}»: применено к ${applied}, пропущено без связок ${skipped}${tail}`);
      } else if (skipped && !applied) {
        notify.warning(`Не применено: у выбранных товаров нет связей с кабинетами в выбранной области (${skipped})`);
      } else {
        notify.success(`«${what}» применено к ${applied} товарам${tail}`);
      }
      onSaved?.();
      onClose();
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось сохранить режимы остатка');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Остаток по складам · {ruPlural(count, 'товар', 'товара', 'товаров')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Режим запишется по выбранным товарам на складские связки кабинетов, к которым они уже привязаны.
            Новые связи не создаются. Для КГТ/МГТ: выберите связку (например, «кгт») и режим «Живой остаток» для КГТ-товаров,
            а связке по умолчанию задайте «Не транслировать» в «Склады» → «Связь с МП».
          </Typography>

          <FormControl>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Режим</Typography>
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
              <FormControlLabel value="live" control={<Radio size="small" />} label="Живой остаток (МС + техкарты)" />
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControlLabel value="forced" control={<Radio size="small" />} label="Принуд" />
                <TextField
                  size="small"
                  type="number"
                  label="Число"
                  value={value}
                  onChange={(e) => { setValue(e.target.value); setMode('forced'); }}
                  disabled={mode !== 'forced'}
                  error={forcedInvalid}
                  inputProps={{ min: 0 }}
                  sx={{ width: 120 }}
                />
              </Stack>
              <FormControlLabel value="none" control={<Radio size="small" />} label="Не транслировать (на МП уходит 0)" />
              <FormControlLabel value="default" control={<Radio size="small" />} label="Как у связки (снять свою настройку)" />
            </RadioGroup>
          </FormControl>

          <FormControl>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Где</Typography>
            <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)}>
              <FormControlLabel value="all" control={<Radio size="small" />} label="Все складские связки кабинетов товара" />
              <FormControlLabel value="selected" control={<Radio size="small" />} label="Только выбранные связки" />
            </RadioGroup>
            {scope === 'selected' && (
              <FormControl size="small" sx={{ mt: 1 }} error={scopeInvalid}>
                <InputLabel id="bulk-bindings">Складские связки</InputLabel>
                <Select
                  labelId="bulk-bindings"
                  label="Складские связки"
                  multiple
                  value={bindingIds}
                  onChange={(e) => setBindingIds(Array.isArray(e.target.value) ? e.target.value : [])}
                  renderValue={(selected) => selected
                    .map((id) => bindingById.get(Number(id)))
                    .filter(Boolean)
                    .map((b) => `${b.name} · ${b.account_name || b.account_id}`)
                    .join(', ')}
                  disabled={loadingBindings}
                >
                  {groups.flatMap((g) => [
                    <ListSubheader key={`h:${g.key}`}>
                      {g.account_name} · {MP_LABEL[g.marketplace] || g.marketplace}{g.account_active ? '' : ' · неактивен'}
                    </ListSubheader>,
                    ...g.bindings.map((b) => (
                      <MenuItem key={b.id} value={Number(b.id)}>
                        <Checkbox size="small" checked={bindingIds.includes(Number(b.id))} sx={{ p: 0.5, mr: 1 }} />
                        {b.name}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          по умолчанию: {b.default_mode === 'none' ? 'не транслировать' : 'живой'}
                        </Typography>
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>
            )}
          </FormControl>

          <FormControlLabel
            control={<Checkbox size="small" checked={includeVariants} onChange={(e) => setIncludeVariants(e.target.checked)} />}
            label="Включая модификации выбранных товаров (связи обычно на модификациях)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отмена</Button>
        <Button variant="contained" onClick={apply} disabled={saving || !count || forcedInvalid || scopeInvalid}>
          {saving ? 'Сохраняем…' : `Применить к ${count}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
